// add-brand.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { BrandService } from 'app/services/brand/brand.service';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';
import { ImageItem } from 'app/services/gallery/gallery.service';

@Component({
  selector: 'app-add-brands',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,ImageUrlPipe,GalleryPickerComponent,ImageFallbackDirective],
  templateUrl: './add-brands.component.html',
  styleUrls: ['./add-brands.component.scss']
})
export class AddBrandsComponent implements OnInit {
  brandForm!: FormGroup;
  submitted = false;
  isLoading = false;
  loadingMessage = '';
  selectedFile: File | null = null;
  logoPreview: string | ArrayBuffer | null = null;
  isGalleryModalOpen = false;
  selectedPhotos: ImageItem[] = [];
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private brandService: BrandService
  ) {}

  ngOnInit() {
    this.initializeForm();
  }

  private initializeForm() {
    this.brandForm = this.fb.group({
      code: ['', [
        Validators.required, 
        Validators.minLength(2), 
        Validators.maxLength(20),
        Validators.pattern('^[a-zA-Z0-9_-]+$')
      ]],
      arName: ['', [
        Validators.required, 
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      enName: ['', [
        Validators.required, 
        Validators.minLength(2),
        Validators.maxLength(100)
      ]],
      logo: [null],
      top: [false, [Validators.required]],
      slug: ['', [
        Validators.required,
        Validators.pattern('^[a-z0-9-]+$')
      ]],
      status: [true, [Validators.required]]
    });
  }

  // الدوال المساعدة للوصول إلى عناصر الفورم
  get f(): { [key: string]: AbstractControl } {
    return this.brandForm.controls;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.brandForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.brandForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'هذا الحقل مطلوب';
      }
      if (field.errors['minlength']) {
        return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
      }
      if (field.errors['maxlength']) {
        return `الحد الأقصى للحروف هو ${field.errors['maxlength'].requiredLength}`;
      }
      if (field.errors['pattern']) {
        if (fieldName === 'code') {
          return 'يجب أن يحتوي الكود على أرقام فقط';
        } else if (fieldName === 'slug') {
          return 'يجب أن يحتوي الرابط على أحرف إنجليزية صغيرة وأرقام وشرطة فقط';
        }
      }
    }
    return '';
  }

  // معالجة اختيار ملف اللوجو
  onLogoSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // التحقق من نوع الملف
      if (!file.type.match('image.*')) {
        alert('❌ يرجى اختيار ملف صورة فقط');
        return;
      }

      // التحقق من حجم الملف (2MB كحد أقصى)
      if (file.size > 2 * 1024 * 1024) {
        alert('❌ حجم الصورة يجب أن يكون أقل من 2MB');
        return;
      }

      this.selectedFile = file;
      this.brandForm.patchValue({ logo: file });

      // عرض معاينة الصورة
      const reader = new FileReader();
      reader.onload = () => {
        this.logoPreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  // إزالة اللوجو
  removeLogo(): void {
    this.selectedFile = null;
    this.logoPreview = null;
    this.brandForm.patchValue({ logo: null });
    
    // إعادة تعيين input file
    const fileInput = document.getElementById('logo') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // إنشاء البراند
  onSubmit(): void {
    this.submitted = true;

    if (!this.brandForm.valid) {
      this.markFormGroupTouched(this.brandForm);
      alert('❌ يوجد أخطاء في النموذج. راجع الحقول المطلوبة.');
      return;
    }

    this.createBrand();
  }

  private createBrand(): void {
    this.isLoading = true;
    this.loadingMessage = 'جاري إنشاء البراند...';

    const brandData = this.brandForm.value;

    console.log('🟡 Creating brand with data:', brandData);

    this.brandService.createBrand(brandData).subscribe({
      next: (response) => {
        console.log('✅ Brand created successfully:', response);
        this.isLoading = false;
        
        if (response.success) {
          alert('✅ تم إنشاء البراند بنجاح!');
          this.router.navigate(['/admin/brands']);
        } else {
          alert('❌ ' + ('حدث خطأ أثناء إنشاء البراند'));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error creating brand:', error);
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }


  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ أثناء إنشاء البراند';
    
    if (error.error && error.error.errors) {
      const validationErrors = error.error.errors;
      const errorMessages = [];
      
      for (const field in validationErrors) {
        if (validationErrors.hasOwnProperty(field)) {
          errorMessages.push(...validationErrors[field]);
        }
      }
      
      if (errorMessages.length > 0) {
        errorMessage = '❌ ' + errorMessages.join('\n');
      }
    } else if (error.error && error.error.message) {
      errorMessage = '❌ ' + error.error.message;
    } else if (error.status === 409) {
      errorMessage = '❌ كود البراند موجود مسبقاً';
    }
    
    alert(errorMessage);
  }

  onReset(): void {
    if (confirm('هل تريد إعادة تعيين النموذج؟ سيتم فقدان جميع البيانات.')) {
      this.brandForm.reset({
        code: '',
        arName: '',
        enName: '',
        logo: null,
        top: false,
        slug: '',
        status: true
      });
      this.selectedFile = null;
      this.logoPreview = null;
      this.submitted = false;
    }
  }

  onCancel(): void {
    if (confirm('هل تريد إلغاء التغييرات؟')) {
      this.router.navigate(['/admin/brands']);
    }
  }

  // توليد slug تلقائي من الاسم الإنجليزي
  generateSlug(): void {
    const enName = this.brandForm.get('enName')?.value;
    if (enName) {
      const slug = enName
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '') // إزالة الرموز غير المرغوب فيها
        .replace(/\s+/g, '-') // استبدال المسافات بشرطة
        .replace(/-+/g, '-') // استبدال الشرطات المتعددة بشرطة واحدة
        .substring(0, 50);
      
      this.brandForm.patchValue({ slug });
    }
  }



  // التحقق من صحة الكود
  validateCode(): void {
    const codeControl = this.brandForm.get('code');
    if (codeControl?.value) {
      const code = codeControl.value;
      if (!/[0-9]/.test(code)) {
        codeControl.setErrors({ pattern: true });
      } else {
        if (codeControl.errors?.['pattern']) {
          delete codeControl.errors['pattern'];
          if (Object.keys(codeControl.errors).length === 0) {
            codeControl.setErrors(null);
          }
        }
      }
    }
  }

  // التحقق من صحة الـ slug
  validateSlug(): void {
    const slugControl = this.brandForm.get('slug');
    if (slugControl?.value) {
      const slug = slugControl.value;
      if (!/^[a-z0-9-]+$/.test(slug)) {
        slugControl.setErrors({ pattern: true });
      } else {
        if (slugControl.errors?.['pattern']) {
          delete slugControl.errors['pattern'];
          if (Object.keys(slugControl.errors).length === 0) {
            slugControl.setErrors(null);
          }
        }
      }
    }
  }

  openGalleryModal(): void {
  this.isGalleryModalOpen = true;
}

closeGalleryModal(): void {
  this.isGalleryModalOpen = false;
}

onGalleryImagesChange(images: ImageItem[]): void {
  this.selectedPhotos = images || [];

  const firstImage = this.selectedPhotos[0];

  if (!firstImage) return;

  this.selectedFile = null;
  this.logoPreview = firstImage.url;

  this.brandForm.patchValue({
    logo: Number(firstImage.id)
  });

  this.closeGalleryModal();
}

}